import React, { useCallback } from 'react';
import {
    Form,
    DatePicker,
    Button,
    Card,
    Space,
    message,
    Table,
    Tag,
    Select,
    Input,
    Skeleton
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { PricingResultType } from '../graphql/mutations/pricingQueryMutations';
import {
    GET_ALL_PRICING_NODE_ENUMS,
    useFilteredPricingScenarios,
} from '../../../../graphql/queries/settlement/getSettlementCommonQueries';
import { GET_ALL_FAIL_PRICING_RESULT_ERROR_CODES } from '../graphql/mutations/pricingQueryMutations';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';


export interface ConditionQueryFormValues {
    /** 输入框模式: 逗号分隔多个 clientId */
    clientIdsInput?: string;
    businessDateRange?: [dayjs.Dayjs, dayjs.Dayjs];
    pricingErrorCode?: string;
    pricingNodeCode?: string;
    businessScenarioCode?: number;
    isSuccess?: boolean | null;
}

interface ConditionPricingQueryTabProps {
    onQuery: (input: {
        clientIds?: number[] | null;
        businessAtStart?: string | null;
        businessAtEnd?: string | null;
        pricingErrorCode?: string | null;
        pricingNodeCode?: string | null;
        businessScenarioCode?: number | null;
        isSuccess?: boolean | null;
        page: number;
        pageSize: number;
        cursor?: string | null;
    }) => void;
    onLoadNextBatch: (cursor: string) => void;
    onViewDetail: (trackingNumber: string) => void;
    loading: boolean;
    queryResult: { data: PricingResultType[]; totalCount: number; nextCursor: string | null } | null;
    page: number;
    pageSize: number;
    onPageChange: (page: number, pageSize: number) => void;
}

const ConditionPricingQueryTab: React.FC<ConditionPricingQueryTabProps> = ({
    onQuery,
    onLoadNextBatch,
    onViewDetail,
    loading,
    queryResult,
    page,
    pageSize,
    onPageChange
}) => {
    const [form] = Form.useForm<ConditionQueryFormValues>();
    const { data: pricingNodeData, loading: pricingNodeLoading } = useQuery(GET_ALL_PRICING_NODE_ENUMS);
    const { data: errorCodeData, loading: errorCodeLoading } = useQuery(GET_ALL_FAIL_PRICING_RESULT_ERROR_CODES);
    const hasIncomeQuery = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_PRICING_QUERY]);
    const hasDspQuery = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PRICING_QUERY]);
    const canQuery = hasIncomeQuery || hasDspQuery;
    const { scenarioOptions, loading: scenarioLoading } = useFilteredPricingScenarios(hasIncomeQuery, hasDspQuery);

    const pricingNodeOptions = pricingNodeData?.getAllPricingNodeEnums?.map((node: { code: string; description: string }) => ({
        value: node.code,
        label: node.description
    })) || [];
    const errorCodeOptions = errorCodeData?.getAllFailPricingResultErrorCodes?.map((item: { code: string; message: string }) => ({
        value: item.code,
        label: `${item.code} - ${item.message}`
    })) || [];

    const runQuery = useCallback((values: ConditionQueryFormValues, pageNum: number = 1, size: number = pageSize) => {
        const range = values.businessDateRange;
        const rawIds = (values.clientIdsInput || '')
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
        const clientIdsNums = rawIds
            .map((id) => Number(String(id).trim()))
            .filter((n) => Number.isFinite(n));
        const clientIds = clientIdsNums.length ? clientIdsNums : null;
        onQuery({
            clientIds,
            businessAtStart: range?.[0] ? range[0].format('YYYY-MM-DD HH:mm:ss') : null,
            businessAtEnd: range?.[1] ? range[1].format('YYYY-MM-DD HH:mm:ss') : null,
            pricingErrorCode: values.pricingErrorCode || null,
            pricingNodeCode: values.pricingNodeCode || null,
            businessScenarioCode: values.businessScenarioCode ?? null,
            isSuccess: values.isSuccess ?? null,
            page: pageNum,
            pageSize: size
        });
    }, [onQuery, pageSize]);

    const handleSubmit = (values: ConditionQueryFormValues) => {
        runQuery(values, 1, pageSize);
    };

    const handleReset = () => {
        form.resetFields();
    };

    const getPricingNodeDescription = (code: string | null | undefined) => {
        if (!code) return '-';
        const node = pricingNodeOptions.find((o: { value: string }) => o.value === code);
        return node ? node.label : code;
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
        { title: '运单号', dataIndex: 'businessId', key: 'businessId', width: 150 },
        { title: '外部订单号', dataIndex: 'otherBusinessId', key: 'otherBusinessId', width: 140 },
        { title: '客户ID', dataIndex: 'clientId', key: 'clientId', width: 90 },
        { title: '业务代码', dataIndex: 'businessCode', key: 'businessCode', width: 90 },
        { title: '业务场景代码', dataIndex: 'businessScenarioCode', key: 'businessScenarioCode', width: 110 },
        { title: '业务费用项', dataIndex: 'businessFeeCode', key: 'businessFeeCode', width: 110 },
        {
            title: '计费节点',
            dataIndex: 'pricingNodeCode',
            key: 'pricingNodeCode',
            width: 120,
            render: (code: string | null | undefined) => getPricingNodeDescription(code)
        },
        {
            title: '计费金额(美分)',
            dataIndex: 'amount',
            key: 'amount',
            width: 110,
            render: (amount: number) => amount?.toLocaleString() || '0'
        },
        {
            title: '状态',
            dataIndex: 'isSuccess',
            key: 'isSuccess',
            width: 80,
            render: (isSuccess: boolean) => {
                const color = isSuccess ? 'green' : 'red';
                const text = isSuccess ? '计费成功' : '计费失败';
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: '定价时间',
            dataIndex: 'pricingAt',
            key: 'pricingAt',
            width: 110,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: '业务时间',
            dataIndex: 'businessAt',
            key: 'businessAt',
            width: 110,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            fixed: 'right' as const,
            render: (_: unknown, record: PricingResultType) => (
                <Button type="link" size="small" onClick={() => onViewDetail(record.businessId)}>
                    查询详情
                </Button>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <Card title="按条件查询计费结果" className="mt-4">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Form.Item label="客户" name="clientIdsInput">
                            <Input
                                placeholder="输入客户ID，多个请用逗号分隔（如: 1001,1002）"
                                allowClear
                            />
                        </Form.Item>
                        <Form.Item label="业务时间" name="businessDateRange">
                            <DatePicker.RangePicker
                                style={{ width: '100%' }}
                                showTime={{ format: 'HH:mm:ss' }}
                                format="YYYY-MM-DD HH:mm:ss"
                                placeholder={['开始时间（UTC）', '结束时间（UTC）']}
                            />
                        </Form.Item>
                        <Form.Item label="计费失败错误码" name="pricingErrorCode">
                            <Select
                                placeholder="请选择"
                                allowClear
                                loading={errorCodeLoading}
                                options={errorCodeOptions}
                            />
                        </Form.Item>
                        <Form.Item label="计费节点" name="pricingNodeCode">
                            <Select
                                placeholder="请选择计费节点"
                                allowClear
                                loading={pricingNodeLoading}
                                options={pricingNodeOptions}
                            />
                        </Form.Item>
                        <Form.Item label="业务场景" name="businessScenarioCode">
                            <Select
                                placeholder="请选择业务场景"
                                allowClear
                                loading={scenarioLoading}
                                options={scenarioOptions}
                            />
                        </Form.Item>
                        <Form.Item label="计费是否成功" name="isSuccess">
                            <Select
                                placeholder="全部"
                                allowClear
                                options={[
                                    { value: null, label: '全部' },
                                    { value: true, label: '成功' },
                                    { value: false, label: '失败' }
                                ]}
                            />
                        </Form.Item>
                    </div>
                    <div className="mt-6">
                        <Space>
                            {canQuery && (
                                <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} size="large">
                                    查询
                                </Button>
                            )}
                            <Button onClick={handleReset} size="large">
                                重置
                            </Button>
                        </Space>
                    </div>
                </Form>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-blue-800 font-medium mb-2">说明</h4>
                    <ul className="text-blue-700 text-sm space-y-1">
                        <li>• 不填条件时点击查询可查看全部数据（分页）</li>
                        <li>• 客户可填多个 ID（标签模式），不填则不限客户</li>
                        <li>• 业务时间为日期范围，精确到天</li>
                        <li>• 点击「查询详情」可跳转到按单号查询页查看该运单计费详情</li>
                    </ul>
                </div>
            </Card>

            {loading && (
                <Card title="查询结果">
                    <Skeleton active paragraph={{ rows: 8 }} />
                </Card>
            )}

            {!loading && queryResult && (
                <Card title={`查询结果${queryResult.totalCount >= 0 ? ` (共 ${queryResult.totalCount} 条，已加载 ${queryResult.data.length} 条)` : ''}`}>
                    <Table
                        columns={columns}
                        dataSource={queryResult.data.slice((page - 1) * pageSize, page * pageSize)}
                        rowKey="id"
                        scroll={{ x: 1400 }}
                        pagination={{
                            current: page,
                            pageSize,
                            total: queryResult.totalCount,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (p, size) => {
                                const nextSize = size || pageSize;
                                onPageChange(p, nextSize);
                                const needEndIndex = p * nextSize;
                                if (needEndIndex > queryResult.data.length && queryResult.nextCursor) {
                                    onLoadNextBatch(queryResult.nextCursor);
                                }
                            }
                        }}
                        size="small"
                    />
                </Card>
            )}

            {!loading && queryResult && queryResult.data.length === 0 && (
                <Card>
                    <div className="text-center py-8 text-gray-500">
                        <SearchOutlined className="text-4xl mb-4" />
                        <p>未找到符合条件的计费记录</p>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ConditionPricingQueryTab;
